/**
 * @file RegionDropdown.js
 * @description A dropdown component for selecting a region.
 */

import React, { useState, useEffect } from 'react';
import { fetchRegions } from '../../../services/orgLevelService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const RegionDropdown = ({
    className,
    inlineLabel = true,
    tenantId,
    selectedRegion,
    onRegionChange,
    isPublic = false,
    companyCode = null,
    required = false,
    errorMessage = null,
}) => {
    const [regions, setRegions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getRegions = async () => {
            setIsLoading(true);
            try {
                const data = await fetchRegions(tenantId, {
                    isPublic,
                    companyCode,
                });
                setRegions(data);
            } catch (error) {
                console.error('Could not fetch regions:', error);
                Toast.error(getText('msg_a3f57465cad7'));
            } finally {
                setIsLoading(false);
            }
        };

        getRegions();
    }, [tenantId, isPublic, companyCode]);

    const getDefaultLabel = () => {
        const matchingRegion = regions.find(
            region => region.id === selectedRegion,
        );
        return matchingRegion ? matchingRegion.name : 'Select Region';
    };

    const regionOptions = [
        { value: null, label: getText('msg_570539ea5ecc') },
        ...regions.map(format => ({
            value: format.id,
            label: format.name,
        })),
    ];

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="region-dropdown">
                {getText('msg_0f217179940c')}
                {required && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="region-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={regionOptions}
                    value={selectedRegion}
                    onChange={onRegionChange}
                    disabled={regions.length === 0 || isLoading}
                />
            </LoadingWrapper>
            {errorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </div>
    );
};

export default RegionDropdown;
